import { useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import { appQueryParams } from '../appParams'
import ReplayErrorModal from './ReplayErrorModal'
import { REPLAY_ERRORS, REPLAY_ERROR_IDS, ReplayErrorId } from './replayErrors'
import { replayErrorModalState, hideReplayError, runReplayErrorAction } from './replayErrorState'

// Public state / API (showReplayError, hideReplayError, classifyReplayError, …)
// live in ./replayErrorState so load-time code can trigger modals without
// importing this React component. This file only renders the state.

// ── Preview helpers (?replayErrorPreview=<id|1>) ───────────────────────────

const PREVIEW_VARS = { version: '1.21.4', maxVersion: '1.21.1' }
const previewCode = (prefix: string) => `${prefix}-260630-A3F9K2`

const tbBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 22,
  border: '1px solid rgba(150,180,195,0.3)',
  background: 'rgba(120,140,160,0.1)',
  color: '#dfe8f0',
  font: '13px var(--mono)',
  cursor: 'pointer',
}

export default function ReplayErrorModalProvider () {
  const snap = useSnapshot(replayErrorModalState)

  const previewParam = appQueryParams.replayErrorPreview
  const previewActive = previewParam !== undefined && previewParam !== '' && previewParam !== '0'
  const [previewIdx, setPreviewIdx] = useState(() => {
    const i = REPLAY_ERROR_IDS.indexOf(previewParam as ReplayErrorId)
    return i >= 0 ? i : 0
  })
  const [previewClosed, setPreviewClosed] = useState(false)

  const cycle = (delta: number) => setPreviewIdx(i => (i + delta + REPLAY_ERROR_IDS.length) % REPLAY_ERROR_IDS.length)

  useEffect(() => {
    if (!previewActive || previewClosed) return
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
          cycle(1)
          break
        case 'ArrowLeft':
          cycle(-1)
          break
        case 'Escape':
          setPreviewClosed(true)
          break
        default:
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [previewActive, previewClosed])

  if (previewActive && !previewClosed) {
    const errorId = REPLAY_ERROR_IDS[previewIdx]
    const def = REPLAY_ERRORS[errorId]
    const toolbar = (
      <div style={{
        position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1400,
        display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
        border: '1px solid var(--replay-metal-edge)', background: 'rgba(7,12,22,0.92)',
        color: '#cdd8e2', font: '11px var(--mono)', letterSpacing: '0.4px',
      }}>
        <button type="button" style={tbBtn} onClick={() => cycle(-1)} aria-label="Предыдущая">◀</button>
        <span style={{ minWidth: 200, textAlign: 'center' }}>
          <span style={{ color: 'var(--gold)' }}>{errorId}</span>
          <span style={{ opacity: 0.6 }}>{`  ${previewIdx + 1}/${REPLAY_ERROR_IDS.length}`}</span>
        </span>
        <button type="button" style={tbBtn} onClick={() => cycle(1)} aria-label="Следующая">▶</button>
        <button type="button" style={{ ...tbBtn, width: 22, color: '#e0a0a0' }} onClick={() => setPreviewClosed(true)} aria-label="Закрыть">✕</button>
      </div>
    )
    return (
      <ReplayErrorModal
        key={errorId}
        errorId={errorId}
        vars={PREVIEW_VARS}
        code={def.codePrefix ? previewCode(def.codePrefix) : undefined}
        onAction={kind => console.log('[replay-error-preview] action:', kind)}
        previewSlot={toolbar}
      />
    )
  }

  if (!snap.open) return null

  return (
    <ReplayErrorModal
      errorId={snap.errorId}
      vars={snap.vars as Record<string, string> | undefined}
      code={snap.code}
      onAction={kind => {
        if (replayErrorModalState.onAction) {
          replayErrorModalState.onAction(kind)
        } else {
          runReplayErrorAction(kind)
        }
        if (kind === 'home') hideReplayError()
      }}
    />
  )
}
